import { useEffect } from "react";
import { logout, tokenRefreshed } from "features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "features/store";
import {
  ensureFreshAccessToken,
  msUntilRefreshDue,
  readStoredAccessToken,
  readStoredRefreshToken,
} from "features/auth/tokenRefresh";

/**
 * Keeps this tab's session alive and in step with the other tabs (CAL-5974).
 *
 * Two jobs:
 *
 * 1. Refresh before the access token expires. The timer is scheduled off the
 *    token's own `exp`, and a browser that throttled or froze the tab is caught
 *    on the way back in - a background tab's timers are slowed to once a minute
 *    and a sleeping laptop fires none at all, which is why waking up used to
 *    mean a 401.
 * 2. Follow what the other tabs do. They share one localStorage, so a refresh
 *    over there has to land in this tab's Redux (that is what sets the auth
 *    header), and a logout over there has to end the session here instead of
 *    leaving an authenticated-looking UI behind a dead token.
 */
export function useSessionKeepAlive() {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isAuthenticated) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const schedule = () => {
      if (stopped || timer !== null) return;
      // A minimum delay keeps a token the server already considers stale from
      // spinning this into a tight loop.
      timer = setTimeout(run, Math.max(msUntilRefreshDue(), 5000));
    };

    const run = async () => {
      timer = null;
      if (stopped) return;
      const outcome = await ensureFreshAccessToken(dispatch);
      if (stopped) return;
      if (outcome === "dead") {
        dispatch(logout());
        return;
      }
      // "transient" reschedules like the rest: the network will come back.
      schedule();
    };

    const refreshIfDue = () => {
      if (msUntilRefreshDue() === 0) {
        void run();
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") refreshIfDue();
    };

    schedule();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refreshIfDue);
    window.addEventListener("online", refreshIfDue);

    return () => {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refreshIfDue);
      window.removeEventListener("online", refreshIfDue);
    };
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    // Fires in the OTHER tabs only, so this is purely "follow the neighbour".
    const onStorage = (event: StorageEvent) => {
      if (event.key !== "accessToken" && event.key !== "refreshToken") return;

      const access = readStoredAccessToken();
      const refresh = readStoredRefreshToken();
      if (!access || !refresh) {
        // A neighbour logged out or its session died; the tokens are gone for
        // everyone. Drop ours locally - no request, nothing left to revoke.
        dispatch(logout());
        return;
      }
      dispatch(tokenRefreshed({ access, refresh }));
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch]);
}

export default useSessionKeepAlive;
